import dayjs, { type Dayjs } from 'dayjs';
import {
	addDays,
	dateFormatData,
	dateFormatHomeDisplay,
	formatDate,
} from 'features/dates/date-helpers';
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from 'react-icons/md';

import CalendarIcon from '@mui/icons-material/Event';
import { IconButton } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import type { VirtualElement } from '@popperjs/core';
import styles from 'common/_variables.module.scss';
import { METRICS_DURATION } from 'pages/home/helpers';
import { useState } from 'react';
import { FaRegCalendarCheck } from 'react-icons/fa';
import { GroupIcon } from '../group-icon';
import type { View } from '../inventory-rate-level-widget/inventory-rate-level-widget';

type HomeDashboardControlsProps = {
	selectedDate: string;
	setDate: (date: string) => void;
	setView: (view: View) => void;
	eventId?: string | null;
	groupBlocked?: number;
	groupBooked?: number;
};

export const HomeDashboardControls = ({
	selectedDate,
	setDate,
	setView,
	eventId,
	groupBlocked = 0,
	groupBooked = 0,
}: HomeDashboardControlsProps) => {
	const [isDatePickerOpen, setIsOpen] = useState(false);
	const [anchorEl, setAnchorEl] = useState<VirtualElement | null>(null);

	const isTodaySelected = dayjs().isSame(selectedDate, 'day');
	const isLastDaySelected = addDays(METRICS_DURATION - 1).isSame(
		selectedDate,
		'day',
	);

	const updateDate = (direction: 'prev' | 'next') => {
		const operation = direction === 'prev' ? 'subtract' : 'add';
		setDate(dayjs(selectedDate)[operation](1, 'day').format(dateFormatData));
	};

	return (
		<div className='tw-flex tw-flex-col'>
			<div className='tw-flex tw-items-center tw-h-4'>
				<IconButton
					aria-label='previous'
					className='tw-p-0 tw-text-[#905959]'
					disabled={isTodaySelected}
					onClick={() => updateDate('prev')}
					data-testid='prevButton'
				>
					<MdKeyboardArrowLeft />
				</IconButton>
				<span className='tw-text-lg tw-text-[#905959] tw-mr-1'>
					{formatDate(selectedDate, dateFormatHomeDisplay)}
				</span>
				<DatePicker
					open={isDatePickerOpen}
					onClose={() => setIsOpen(false)}
					value={selectedDate}
					onChange={(newDate: Dayjs | null) => {
						newDate && setDate(newDate.format(dateFormatData));
					}}
					disablePast
					maxDate={addDays(METRICS_DURATION)}
					PopperProps={{
						placement: 'top',
						anchorEl: anchorEl,
					}}
					renderInput={() => (
						<IconButton
							aria-label='pick a date'
							className='tw-p-0 tw-text-[#905959]'
							disableFocusRipple
							disableRipple
							onClick={(e) => {
								setIsOpen((isOpen) => !isOpen);
								setAnchorEl(e.currentTarget);
							}}
						>
							<CalendarIcon />
						</IconButton>
					)}
				/>
				<IconButton
					aria-label='next'
					className='tw-p-0 tw-text-[#905959]'
					disabled={isLastDaySelected}
					onClick={() => updateDate('next')}
					data-testid='nextButton'
				>
					<MdKeyboardArrowRight />
				</IconButton>
				{eventId && (
					<IconButton
						aria-label='event'
						className='tw-p-0 tw-text-[#905959] tw-ml-3'
						onClick={() => console.log('TODO: Add functionality')}
					>
						<FaRegCalendarCheck color={styles.colorOrange} size={18} />
					</IconButton>
				)}
				<GroupIcon
					groupBooked={groupBooked}
					groupBlocked={groupBlocked}
					setView={setView}
				/>
			</div>
		</div>
	);
};
